<template>
  <div class="mining-panel" data-component-id="insights--panel--mining">

    <!-- Child tabs switcher -->
    <div class="mp-tabs" role="tablist">
      <button
        class="mp-tab-btn"
        :class="{ active: activeTab === 'runs' }"
        @click="activeTab = 'runs'"
        role="tab"
        :aria-selected="activeTab === 'runs'"
        data-component-id="insights--tab--runs"
      >🚀 Mining Runs</button>
      <button
        class="mp-tab-btn"
        :class="{ active: activeTab === 'browser' }"
        @click="activeTab = 'browser'"
        role="tab"
        :aria-selected="activeTab === 'browser'"
        data-component-id="insights--tab--browser"
      >📚 Insights Browser</button>
    </div>

    <!-- Browser tab — lazy mounted so polling tetap terbatas di Runs tab -->
    <InsightsBrowserTab v-if="activeTab === 'browser'" />

    <!-- Runs tab — original mining panel content -->
    <div v-show="activeTab === 'runs'" class="mp-tab-pane">

    <!-- Backend status pill -->
    <div class="mp-backend" v-if="backend">
      <span class="mp-backend-dot" :class="`dot-${backend.provider}`"></span>
      <span class="mp-backend-text">
        <strong>{{ backend.provider === 'anthropic' ? 'Anthropic' : 'Local LLM' }}</strong>
        · {{ backend.model }}
      </span>
      <span v-if="backend.baseUrl" class="mp-backend-url" :title="backend.baseUrl">
        @ {{ backend.baseUrl.replace(/^https?:\/\//, '').slice(0, 28) }}
      </span>
    </div>

    <!-- ── Form: kick off new run ─────────────────────────────────────── -->
    <details class="mp-form-wrap" :open="!hasActiveRuns">
      <summary class="mp-form-summary">
        <span>🚀 Mulai Mining Run Baru</span>
        <span class="mp-summary-hint">{{ hasActiveRuns ? 'klik untuk buka form' : '' }}</span>
      </summary>

      <div class="mp-form">
        <div class="mp-row">
          <label class="mp-label">Kategori</label>
          <div class="mp-cats">
            <label
              v-for="cat in availableCategories"
              :key="cat"
              class="mp-cat-chip"
              :class="{ checked: selectedCategories.includes(cat) }"
            >
              <input
                type="checkbox"
                :value="cat"
                v-model="selectedCategories"
              />
              {{ cat }}
            </label>
          </div>
          <div class="mp-cat-actions">
            <button class="mp-mini" @click="selectedCategories = [...availableCategories]">Pilih semua</button>
            <button class="mp-mini" @click="selectedCategories = []">Kosongkan</button>
          </div>
        </div>

        <div class="mp-row mp-grid">
          <div>
            <label class="mp-label">Days Back</label>
            <input class="mp-input" type="number" min="1" max="365" v-model.number="daysBack" />
            <span class="mp-hint">window pesan WA, 90 = 3 bulan</span>
          </div>
          <div>
            <label class="mp-label">Sample / pass</label>
            <input class="mp-input" type="number" min="10" max="200" v-model.number="sampleSize" />
            <span class="mp-hint">200 sampel/pass = batas Qwen 16K</span>
          </div>
          <div>
            <label class="mp-label">Passes</label>
            <input class="mp-input" type="number" min="1" max="5" v-model.number="passes" />
            <span class="mp-hint">jumlah pengulangan acak</span>
          </div>
        </div>

        <div class="mp-summary-info">
          <span>{{ selectedCategories.length || availableCategories.length }} kategori</span>
          <span>×</span>
          <span>{{ passes }} passes</span>
          <span>=</span>
          <strong>{{ totalCalls }} LLM calls</strong>
        </div>

        <button
          class="mp-btn mp-btn-primary"
          :disabled="starting || !selectedCategories.length"
          @click="startRun"
        >
          {{ starting ? 'Memulai...' : '🚀 Jalankan Mining' }}
        </button>
        <div v-if="startError" class="mp-error">{{ startError }}</div>
      </div>
    </details>

    <!-- ── Active / recent runs list ──────────────────────────────────── -->
    <div class="mp-runs-header">
      <span>Riwayat Run</span>
      <span class="mp-poll-badge" v-if="polling">🔄 polling tiap {{ pollIntervalMs/1000 }}s</span>
      <button class="mp-mini" @click="refreshRuns">↻ Refresh</button>
    </div>

    <div v-if="loadError" class="mp-error">{{ loadError }}</div>
    <div v-if="!runs.length && !loading" class="mp-empty">
      Belum ada mining run. Jalankan run pertama untuk mulai menambang insight.
    </div>

    <ul class="mp-run-list">
      <li
        v-for="r in runs"
        :key="r.runId"
        class="mp-run-item"
        :class="['status-' + r.status, { selected: selectedRunId === r.runId, stuck: r.isStuck }]"
        @click="selectRun(r.runId)"
      >
        <div class="mp-run-top">
          <span class="mp-status-pill" :class="'pill-' + r.status">
            {{ statusLabel(r.status) }}
          </span>
          <span v-if="r.isStuck" class="mp-stuck-pill">⚠️ stuck</span>
          <span class="mp-run-id">{{ r.runId.slice(-8) }}</span>
          <span class="mp-run-time">{{ formatRelative(r.startedAt) }}</span>
          <button
            v-if="canCancel(r)"
            class="mp-stop-btn"
            :disabled="cancelling === r.runId"
            @click.stop="stopRun(r.runId)"
            :title="r.progress.lastEvent === 'cancel requested' ? 'Cancel sudah dikirim — menunggu call current selesai' : 'Hentikan mining run'"
          >
            {{ cancelling === r.runId ? '⏳ Stopping…' : '⏸ Stop' }}
          </button>
        </div>
        <div class="mp-run-progress">
          <div class="mp-progress-bar">
            <div class="mp-progress-fill" :style="{ width: progressPct(r) + '%' }"></div>
          </div>
          <span class="mp-progress-text">
            {{ r.progress.completedCalls }} / {{ r.progress.totalCalls }}
            · +{{ r.totalInsightsCreated }} insight
            <span v-if="r.runsErrored" class="mp-err-tag">· {{ r.runsErrored }} error</span>
          </span>
        </div>
        <div class="mp-run-event" :title="r.progress.lastEvent">
          {{ r.progress.lastEvent }}
        </div>
      </li>
    </ul>

    <!-- ── Selected run detail ───────────────────────────────────────── -->
    <div v-if="selectedRun" class="mp-detail">
      <div class="mp-detail-head">
        <strong>Detail: {{ selectedRun.runId }}</strong>
        <div class="mp-detail-actions">
          <button
            v-if="canCancel(selectedRun)"
            class="mp-stop-btn"
            :disabled="cancelling === selectedRun.runId"
            @click="stopRun(selectedRun.runId)"
          >
            {{ cancelling === selectedRun.runId ? '⏳ Stopping…' : '⏸ Stop Run' }}
          </button>
          <button class="mp-mini" @click="selectedRunId = null">tutup</button>
        </div>
      </div>

      <div class="mp-detail-grid">
        <div><span>Status</span><strong>{{ statusLabel(selectedRun.status) }}</strong></div>
        <div><span>Backend</span><strong>{{ selectedRun.backend?.provider || '-' }}</strong></div>
        <div><span>Model</span><strong class="mono">{{ truncate(selectedRun.backend?.model, 32) }}</strong></div>
        <div><span>Created by</span><strong>{{ selectedRun.createdBy }}</strong></div>
        <div><span>Started</span><strong>{{ formatTime(selectedRun.startedAt) }}</strong></div>
        <div><span>Last tick</span><strong>{{ formatTime(selectedRun.lastTickAt) }}</strong></div>
        <div v-if="selectedRun.finishedAt"><span>Finished</span><strong>{{ formatTime(selectedRun.finishedAt) }}</strong></div>
        <div v-if="selectedRun.durationMs"><span>Durasi</span><strong>{{ (selectedRun.durationMs/1000).toFixed(1) }}s</strong></div>
      </div>

      <div class="mp-config-row">
        <span class="mp-cfg">📂 {{ selectedRun.config.categories.join(', ') }}</span>
        <span class="mp-cfg">📅 {{ selectedRun.config.daysBack }}d</span>
        <span class="mp-cfg">📊 {{ selectedRun.config.sampleSize }} sampel × {{ selectedRun.config.passes }} pass</span>
      </div>

      <div v-if="selectedRun.error" class="mp-error mp-error-detail">
        <strong>Error:</strong> {{ selectedRun.error }}
      </div>

      <h4 class="mp-section-title">Per-Call Log ({{ selectedRun.calls?.length || 0 }})</h4>
      <ul class="mp-call-list">
        <li
          v-for="(call, idx) in selectedRun.calls"
          :key="idx"
          class="mp-call-item"
          :class="{ ok: !call.error, err: !!call.error }"
        >
          <span class="mp-call-cat">{{ call.category }}</span>
          <span class="mp-call-pass">p{{ call.pass }}</span>
          <span v-if="call.samplesFetched" class="mp-call-meta">{{ call.samplesFetched }} sampel</span>
          <span v-if="call.insightsCreated >= 0" class="mp-call-meta">→ {{ call.insightsCreated }} insight</span>
          <span v-if="call.durationMs" class="mp-call-meta">· {{ (call.durationMs/1000).toFixed(1) }}s</span>
          <span v-if="call.usage?.input_tokens" class="mp-call-meta token">
            tok in:{{ call.usage.input_tokens }} out:{{ call.usage.output_tokens || 0 }}
          </span>
          <span v-if="call.error" class="mp-call-error" :title="call.error">
            ✗ {{ truncate(call.error, 60) }}
          </span>
          <details v-if="call.insights?.length" class="mp-call-insights">
            <summary>{{ call.insights.length }} subject</summary>
            <ul>
              <li v-for="(ins, i) in call.insights" :key="i">
                <span class="mp-ins-conf" :class="'conf-'+(ins.confidence||'low')">{{ ins.confidence?.[0] || '?' }}</span>
                <code>{{ ins.subject }}</code>
                <span v-if="ins.insightId" class="mp-ins-id">#{{ ins.insightId }}</span>
                <em v-if="ins.error" class="mp-ins-err">{{ ins.error }}</em>
              </li>
            </ul>
          </details>
        </li>
      </ul>
    </div>

    </div><!-- /Runs tab pane -->
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import InsightsBrowserTab from './InsightsBrowserTab.vue'

const STORAGE_KEY = 'uprint_auth_token'
const pollIntervalMs = 2500

// ── Tab switching ─────────────────────────────────────────────────────
// Default ke Runs tab. Browser di-mount lazy (v-if) supaya hanya kena
// fetch saat user buka tab-nya — Runs tab pakai v-show agar polling state
// tidak hilang saat user pindah tab.
const activeTab = ref('runs')

// ── Form state ────────────────────────────────────────────────────────
const availableCategories = ref([])
const selectedCategories = ref([])
const daysBack    = ref(90)
const sampleSize  = ref(150)
const passes      = ref(2)
const starting    = ref(false)
const startError  = ref(null)

// ── Run list state ────────────────────────────────────────────────────
const runs           = ref([])
const backend        = ref(null)
const loading        = ref(false)
const loadError      = ref(null)
const selectedRunId  = ref(null)
const selectedRun    = ref(null)
let   pollTimer      = null
const polling        = ref(false)

const hasActiveRuns = computed(() =>
  runs.value.some(r => r.status === 'running' || r.status === 'pending')
)
const totalCalls = computed(() => {
  const cats = selectedCategories.value.length || availableCategories.value.length
  return cats * (passes.value || 1)
})

function token() {
  return localStorage.getItem(STORAGE_KEY) || ''
}

function progressPct(r) {
  if (!r.progress.totalCalls) return 0
  return Math.min(100, Math.round((r.progress.completedCalls / r.progress.totalCalls) * 100))
}

function statusLabel(s) {
  return ({
    pending:   'Pending',
    running:   'Running',
    done:      'Selesai',
    failed:    'Gagal',
    cancelled: 'Dibatalkan',
  })[s] || s
}

function canCancel(r) {
  return r && (r.status === 'running' || r.status === 'pending')
}

function formatRelative(iso) {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  if (diff < 60_000) return Math.floor(diff/1000) + 's lalu'
  if (diff < 3600_000) return Math.floor(diff/60_000) + 'm lalu'
  if (diff < 86400_000) return Math.floor(diff/3600_000) + 'j lalu'
  return Math.floor(diff/86400_000) + 'h lalu'
}

function formatTime(iso) {
  if (!iso) return '-'
  return new Date(iso).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function truncate(s, n) {
  if (!s) return ''
  return s.length > n ? s.slice(0, n) + '…' : s
}

// ── API calls ─────────────────────────────────────────────────────────

async function refreshRuns() {
  const t = token()
  if (!t) {
    loadError.value = 'Token admin tidak tersedia.'
    return
  }
  loading.value = true
  loadError.value = null
  try {
    const resp = await fetch(`/api/insights/runs?token=${encodeURIComponent(t)}&limit=20`)
    const data = await resp.json()
    if (!data.success) throw new Error(data.message || 'Gagal memuat run list')
    runs.value = data.runs || []
    backend.value = data.backend || null
    if (Array.isArray(data.categories) && data.categories.length) {
      availableCategories.value = data.categories
      if (!selectedCategories.value.length) {
        selectedCategories.value = [...data.categories]
      }
    }
    if (selectedRunId.value) await loadSelectedRun()
  } catch (err) {
    loadError.value = err.message
  } finally {
    loading.value = false
  }
}

async function loadSelectedRun() {
  if (!selectedRunId.value) return
  const t = token()
  try {
    const resp = await fetch(`/api/insights/runs/${selectedRunId.value}?token=${encodeURIComponent(t)}`)
    const data = await resp.json()
    if (data.success && data.run) selectedRun.value = data.run
  } catch (err) {
    /* ignore — list view still shows summary */
  }
}

function selectRun(id) {
  selectedRunId.value = id
  loadSelectedRun()
}

const cancelling = ref(null)
async function stopRun(runId) {
  cancelling.value = runId
  try {
    const t = token()
    const resp = await fetch(`/api/insights/runs/${runId}/cancel?token=${encodeURIComponent(t)}`, {
      method: 'POST',
    })
    const data = await resp.json()
    if (!data.success) throw new Error(data.message || 'Gagal cancel')
    await refreshRuns()
  } catch (err) {
    loadError.value = err.message
  } finally {
    cancelling.value = null
  }
}

async function startRun() {
  starting.value = true
  startError.value = null
  try {
    const t = token()
    const body = {
      token: t,
      categories: selectedCategories.value.length === availableCategories.value.length
        ? null
        : selectedCategories.value,
      daysBack:   daysBack.value,
      sampleSize: sampleSize.value,
      passes:     passes.value,
    }
    const resp = await fetch('/api/insights/extract-all', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const data = await resp.json()
    if (!data.success) throw new Error(data.message || `HTTP ${resp.status}`)
    selectedRunId.value = data.runId
    await refreshRuns()
    ensurePolling()
  } catch (err) {
    startError.value = err.message
  } finally {
    starting.value = false
  }
}

// ── Polling lifecycle ─────────────────────────────────────────────────
function ensurePolling() {
  if (pollTimer) return
  polling.value = true
  pollTimer = setInterval(() => {
    refreshRuns().finally(() => {
      // Stop polling when nothing is active for a while.
      if (!hasActiveRuns.value) stopPolling()
    })
  }, pollIntervalMs)
}

function stopPolling() {
  if (pollTimer) clearInterval(pollTimer)
  pollTimer = null
  polling.value = false
}

watch(hasActiveRuns, (active) => {
  if (active) ensurePolling()
})

onMounted(async () => {
  await refreshRuns()
  if (hasActiveRuns.value) ensurePolling()
})
onUnmounted(stopPolling)
</script>

<style scoped>
.mining-panel {
  font-size: 12px;
  color: #d4d4d4;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 4px;
}

/* Child tabs */
.mp-tabs {
  display: flex;
  gap: 2px;
  border-bottom: 1px solid #2a2a2a;
  padding: 0 2px;
}
.mp-tab-btn {
  background: transparent;
  border: 1px solid transparent;
  border-bottom: none;
  color: #888;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 6px 6px 0 0;
  margin-bottom: -1px;
  transition: background .15s, color .15s;
}
.mp-tab-btn:hover { background: #161616; color: #ccc; }
.mp-tab-btn.active {
  background: #0e0e0e;
  border-color: #2a2a2a;
  border-bottom-color: #0e0e0e;
  color: #22c55e;
}
.mp-tab-pane { display: flex; flex-direction: column; gap: 12px; }

.mp-backend {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: #1a1a1a;
  border: 1px solid #2a2a2a;
  border-radius: 6px;
  font-size: 11px;
}
.mp-backend-dot { width:8px; height:8px; border-radius:50%; background:#888; }
.mp-backend-dot.dot-anthropic { background:#a855f7; }
.mp-backend-dot.dot-openai_compatible { background:#22c55e; }
.mp-backend-url { color: #777; font-family: ui-monospace, monospace; font-size: 10px; }

/* Form */
.mp-form-wrap { background: #161616; border: 1px solid #262626; border-radius: 8px; }
.mp-form-summary {
  cursor: pointer; padding: 10px 12px; user-select: none;
  display: flex; justify-content: space-between; align-items: center;
  font-weight: 600;
}
.mp-summary-hint { font-size: 10px; color: #666; font-weight: normal; }
.mp-form { padding: 0 12px 12px; display: flex; flex-direction: column; gap: 12px; }
.mp-row { display: flex; flex-direction: column; gap: 6px; }
.mp-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; }
.mp-label { font-size: 11px; color: #888; }
.mp-input {
  width: 100%; background:#0d0d0d; border:1px solid #2a2a2a; color:#e5e5e5;
  border-radius:4px; padding:5px 7px; font-size:12px;
}
.mp-hint { font-size: 10px; color: #666; }

.mp-cats { display: flex; flex-wrap: wrap; gap: 4px; }
.mp-cat-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 8px; font-size: 11px; border-radius: 999px;
  background: #0d0d0d; border: 1px solid #2a2a2a; cursor: pointer;
}
.mp-cat-chip.checked { background: #1d3a23; border-color: #22c55e; color: #e5e5e5; }
.mp-cat-chip input { display: none; }

.mp-cat-actions { display: flex; gap: 6px; margin-top: 4px; }
.mp-mini {
  background:#1a1a1a; border:1px solid #2a2a2a; color:#aaa;
  font-size:10px; padding:2px 7px; border-radius:4px; cursor:pointer;
}
.mp-mini:hover { background:#222; color:#e5e5e5; }

.mp-summary-info {
  display: flex; gap: 6px; align-items: center; padding: 6px 0;
  border-top: 1px dashed #2a2a2a; font-size: 11px; color: #aaa;
}
.mp-summary-info strong { color: #22c55e; font-size: 12px; }

.mp-btn {
  padding: 8px 12px; border-radius: 6px; border: none;
  font-size: 12px; font-weight: 600; cursor: pointer;
}
.mp-btn-primary { background:#22c55e; color:#0a0a0a; }
.mp-btn-primary:hover:not(:disabled) { background:#16a34a; }
.mp-btn-primary:disabled { background:#444; color:#888; cursor:not-allowed; }

.mp-error {
  background:#3b1212; border:1px solid #7a1f1f; color:#fca5a5;
  padding:8px 10px; border-radius:5px; font-size:11px;
}
.mp-error-detail { margin: 8px 0; }

/* Runs list */
.mp-runs-header {
  display:flex; align-items:center; gap:10px;
  font-weight:600; padding-top:6px; border-top:1px solid #2a2a2a;
}
.mp-poll-badge { font-size:10px; color:#22c55e; }
.mp-empty { color:#666; padding:20px; text-align:center; font-style: italic; }

.mp-run-list { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:6px; }
.mp-run-item {
  background:#161616; border:1px solid #262626; border-radius:6px;
  padding:8px 10px; cursor:pointer; transition:background .15s;
}
.mp-run-item:hover { background:#1c1c1c; }
.mp-run-item.selected { border-color:#22c55e; }
.mp-run-item.stuck { border-color:#f59e0b; }

.mp-run-top { display:flex; align-items:center; gap:8px; font-size:11px; }
.mp-status-pill {
  padding:1px 8px; border-radius:999px; font-size:10px; font-weight:600;
}
.pill-running   { background:#1e3a8a; color:#93c5fd; }
.pill-pending   { background:#3a2d1e; color:#fbbf24; }
.pill-done      { background:#1d3a23; color:#86efac; }
.pill-failed    { background:#3b1212; color:#fca5a5; }
.pill-cancelled { background:#2a2a2a; color:#a3a3a3; }

.mp-stop-btn {
  background:#3b1212; border:1px solid #7a1f1f; color:#fca5a5;
  font-size:10px; padding:2px 8px; border-radius:4px; cursor:pointer;
  font-weight:600; margin-left:auto;
}
.mp-stop-btn:hover:not(:disabled) { background:#5a1a1a; color:#fee2e2; }
.mp-stop-btn:disabled { opacity:0.5; cursor:wait; }
.mp-detail-actions { display:flex; gap:6px; align-items:center; }
.mp-stuck-pill { background:#7c2d12; color:#fbbf24; padding:1px 6px; border-radius:4px; font-size:10px; }
.mp-run-id  { font-family: ui-monospace, monospace; color:#666; font-size:10px; }
.mp-run-time { margin-left:auto; color:#777; font-size:10px; }

.mp-run-progress { display:flex; align-items:center; gap:8px; margin:6px 0 4px; }
.mp-progress-bar {
  flex:1; height:5px; background:#0d0d0d; border-radius:3px; overflow:hidden;
}
.mp-progress-fill {
  height:100%; background:linear-gradient(90deg,#22c55e,#86efac); transition:width .3s;
}
.mp-progress-text { font-size:10px; color:#aaa; white-space:nowrap; }
.mp-err-tag { color:#fca5a5; }
.mp-run-event {
  font-size:10px; color:#888; font-style:italic;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
}

/* Detail */
.mp-detail {
  background:#0e1f12; border:1px solid #1d3a23; border-radius:8px;
  padding:12px; display:flex; flex-direction:column; gap:8px;
}
.mp-detail-head {
  display:flex; justify-content:space-between; align-items:center;
  font-size:11px; font-family:ui-monospace, monospace;
}
.mp-detail-grid {
  display:grid; grid-template-columns: repeat(2, 1fr); gap:4px 16px; font-size:11px;
}
.mp-detail-grid div { display:flex; justify-content:space-between; gap:8px; }
.mp-detail-grid span { color:#888; }
.mp-detail-grid strong { color:#e5e5e5; font-weight:500; }
.mono { font-family:ui-monospace, monospace; font-size:10px; }

.mp-config-row { display:flex; flex-wrap:wrap; gap:6px; }
.mp-cfg { background:#1a1a1a; padding:3px 8px; border-radius:4px; font-size:10px; color:#aaa; }

.mp-section-title { margin: 8px 0 4px; font-size:11px; color:#888; font-weight:600; }
.mp-call-list { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:4px; }
.mp-call-item {
  background:#0d0d0d; border:1px solid #1a1a1a; border-radius:4px;
  padding:5px 8px; font-size:11px; display:flex; flex-wrap:wrap;
  align-items:center; gap:6px;
}
.mp-call-item.err { border-left: 3px solid #b91c1c; }
.mp-call-item.ok  { border-left: 3px solid #16a34a; }
.mp-call-cat { font-weight:600; color:#86efac; }
.mp-call-pass { color:#666; }
.mp-call-meta { color:#888; font-size:10px; }
.mp-call-meta.token { color:#a78bfa; }
.mp-call-error { color:#fca5a5; font-size:10px; }
.mp-call-insights summary { cursor:pointer; color:#22c55e; font-size:10px; }
.mp-call-insights ul { margin:4px 0 0 12px; padding:0; list-style:none; }
.mp-call-insights li {
  display:flex; align-items:center; gap:6px; font-size:10px; padding:2px 0;
}
.mp-ins-conf {
  width:14px; height:14px; border-radius:50%; display:inline-flex;
  align-items:center; justify-content:center; font-size:9px; font-weight:700;
}
.conf-low    { background:#3a2d1e; color:#fbbf24; }
.conf-medium { background:#1e3a8a; color:#93c5fd; }
.conf-high   { background:#1d3a23; color:#86efac; }
.mp-ins-id { color:#666; font-family:ui-monospace, monospace; }
.mp-ins-err { color:#fca5a5; font-style:italic; }

@media (max-width: 768px) {
  .mp-tabs {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .mp-tabs::-webkit-scrollbar { display: none; }
  .mp-tab-btn {
    flex-shrink: 0;
    min-width: 100px;
  }
  .mp-run-top {
    flex-wrap: wrap;
    gap: 6px;
  }
  .mp-run-time {
    margin-left: 0;
  }
  .mp-detail-head {
    flex-wrap: wrap;
    gap: 8px;
  }
  .mp-detail-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (max-width: 480px) {
  .mp-grid {
    grid-template-columns: 1fr;
  }
  .mp-detail-grid {
    grid-template-columns: 1fr;
  }
  .mp-run-top {
    gap: 4px;
  }
  .mp-run-progress {
    flex-wrap: wrap;
  }
  .mp-progress-text {
    white-space: normal;
  }
  .mp-config-row {
    gap: 4px;
  }
  .mp-cfg {
    font-size: 9px;
    padding: 2px 6px;
  }
}
</style>
